import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Project } from '../../entities/organization.entity';
import { AppUserService } from '../../services/app-user.service';

export interface RequestProjectAccessDialogData {
  organization: string;
  userExistingProjects: string[];
}

@Component({
  selector: 'app-request-project-access-dialog',
  templateUrl: './request-project-access-dialog.component.html',
  styleUrls: ['./request-project-access-dialog.component.scss'],
})
export class RequestProjectAccessDialogComponent implements OnInit {
  isLoading = false;
  isSubmitting = false;
  isSubmitted = false;
  hasError = false;

  allProjects: Project[] = [];
  selectedProjects = new Set<string>();
  searchQuery = '';
  accessReason = '';
  showAlreadyAccessible = false;

  constructor(
    public dialogRef: MatDialogRef<RequestProjectAccessDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RequestProjectAccessDialogData,
    private appUserService: AppUserService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadProjects();
  }

  private async loadProjects(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    try {
      // TODO: replace with org-scoped endpoint when backend is ready
      const result = await this.appUserService.getOrganizationProjects();
      let flat: Project[] = [];

      if (Array.isArray(result)) {
        flat = result as unknown as Project[];
      } else {
        flat = Object.values(
          result as unknown as Record<string, Project[]>
        ).flat();
      }

      const org = (this.data.organization ?? '').trim().toLowerCase();
      this.allProjects = org
        ? flat.filter(
            (p) => (p.organization ?? '').trim().toLowerCase() === org
          )
        : flat;
    } catch {
      this.hasError = true;
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  retryLoad(): void {
    this.loadProjects();
  }

  get filteredProjects(): Project[] {
    const q = this.searchQuery.trim().toLowerCase();
    return this.allProjects.filter(
      (p) =>
        (this.showAlreadyAccessible || !this.alreadyHasAccess(p)) &&
        (!q ||
          (p.project ?? '').toLowerCase().includes(q) ||
          (p.organization ?? '').toLowerCase().includes(q))
    );
  }

  get hiddenAccessibleCount(): number {
    const q = this.searchQuery.trim().toLowerCase();
    return this.allProjects.filter(
      (p) =>
        this.alreadyHasAccess(p) &&
        (!q ||
          (p.project ?? '').toLowerCase().includes(q) ||
          (p.organization ?? '').toLowerCase().includes(q))
    ).length;
  }

  get hasNoNewProjects(): boolean {
    return (
      this.allProjects.length > 0 &&
      this.allProjects.every((p) => this.alreadyHasAccess(p))
    );
  }

  private projectKey(p: Project): string {
    return `${p.project}|${p.organization ?? ''}`;
  }

  toggleSelect(project: Project): void {
    if (this.alreadyHasAccess(project)) return;
    const key = this.projectKey(project);
    if (this.selectedProjects.has(key)) {
      this.selectedProjects.delete(key);
    } else {
      this.selectedProjects.add(key);
    }
  }

  isSelected(project: Project): boolean {
    return this.selectedProjects.has(this.projectKey(project));
  }

  alreadyHasAccess(project: Project): boolean {
    return (this.data.userExistingProjects ?? []).includes(project.project ?? '');
  }

  get canSubmit(): boolean {
    return (
      this.selectedProjects.size > 0 &&
      this.accessReason.trim().length > 0 &&
      !this.isSubmitting
    );
  }

  get selectedCount(): number {
    return this.selectedProjects.size;
  }

  get selectedProjectsList(): Project[] {
    return this.allProjects.filter((p) =>
      this.selectedProjects.has(this.projectKey(p))
    );
  }

  get request_text(): string {
    const projects = this.selectedProjectsList
      .map((p, i) => {
        const number = p.project_number ? ` (${p.project_number})` : '';
        return `${i + 1}. ${p.project}${number}`;
      })
      .join('\n');

    return [
      `Organization: ${this.data.organization}`,
      '',
      'Requested Access Projects:',
      projects,
      '',
      'Reason:',
      this.accessReason.trim(),
    ].join('\n');
  }

  async submitRequest(): Promise<void> {
    if (!this.canSubmit) return;

    this.isSubmitting = true;
    this.hasError = false;

    try {
      const payload = {
        organization: this.data.organization,
        projects: this.selectedProjectsList.map((p) => ({
          project: p.project,
          project_number: p.project_number ?? null,
        })),
        reason: this.accessReason.trim(),
        request_text: this.request_text,
      };

      console.log('[RequestProjectAccess] Payload:', payload);
      console.log('[RequestProjectAccess] Email text:\n' + this.request_text);

      await new Promise<void>((resolve) => setTimeout(resolve, 1200));
      this.isSubmitted = true;
    } catch {
      this.hasError = true;
    } finally {
      this.isSubmitting = false;
      this.cdr.detectChanges();
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
